// A short, cheap proxy clip of the original video. Audio is what makes a
// speaker attributable, so it is kept. The filter chain, fps and bitrates are
// the ones redraw's gemini-media has been using in production
// (pipeline/script_review_skills/gemini-media/scripts/gemini_media.py) — a
// different encode would change what the model can and cannot see.
import { spawn } from "node:child_process";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";

export const CLIP_FPS = 2;
/** The review skill caps a media window at 15s; enforced here, not in a prompt. */
export const MAX_WINDOW_S = 15;

export interface Clip { path: string; bytes: number; cleanup: () => void }

/** `M:SS`, `H:MM:SS`, `12.5s` or bare seconds -> seconds. */
export function seconds(value: string): number {
  const raw = value.trim().replace(/s$/, "");
  if (!raw) throw new Error("timestamp is empty");
  const parts = raw.split(":");
  if (parts.length > 3) throw new Error(`unparseable timestamp: ${value}`);
  return parts.map((p) => {
    const n = Number(p);
    if (!Number.isFinite(n) || n < 0) throw new Error(`unparseable timestamp: ${value}`);
    return n;
  }).reduce((total, n) => total * 60 + n, 0);
}

export function ffmpegArgs(src: string, from: string, to: string, out: string): string[] {
  return [
    "-y", "-nostdin", "-loglevel", "error",
    "-ss", String(seconds(from)), "-to", String(seconds(to)),
    "-i", src,
    "-map", "0:v:0", "-map", "0:a:0?",
    "-vf", `fps=${CLIP_FPS},scale=-2:min(360\\,ih),setsar=1`,
    "-c:v", "libx264", "-preset", "ultrafast", "-crf", "30", "-pix_fmt", "yuv420p",
    "-c:a", "aac", "-b:a", "48k", "-ar", "24000", "-ac", "1",
    "-movflags", "+faststart",
    out,
  ];
}

export async function cutClip(src: string, from: string, to: string): Promise<Clip> {
  const span = seconds(to) - seconds(from);
  if (span <= 0) throw new Error(`window end must follow its start (${from}..${to})`);
  if (span > MAX_WINDOW_S) {
    throw new Error(`window ${from}..${to} is ${span.toFixed(1)}s;`
      + ` a media check covers at most ${MAX_WINDOW_S}s — split it into overlapping windows`);
  }
  if (!fs.existsSync(src)) throw new Error(`source video not found: ${src}`);

  const dir = fs.mkdtempSync(path.join(os.tmpdir(), "review-clip-"));
  const out = path.join(dir, "evidence.mp4");
  const cleanup = () => fs.rmSync(dir, { recursive: true, force: true });
  try {
    await run("ffmpeg", ffmpegArgs(src, from, to, out));
  } catch (error) { cleanup(); throw error; }
  if (!fs.existsSync(out) || fs.statSync(out).size === 0) {
    cleanup();
    throw new Error(`ffmpeg produced no output for ${from}..${to}`);
  }
  return { path: out, bytes: fs.statSync(out).size, cleanup };
}

/** One still. No audio, but precise and cheap enough to ask for several. */
export async function grabFrame(src: string, at: string, out: string): Promise<string> {
  if (!fs.existsSync(src)) throw new Error(`source video not found: ${src}`);
  fs.mkdirSync(path.dirname(out), { recursive: true });
  await run("ffmpeg", [
    "-y", "-nostdin", "-loglevel", "error",
    "-ss", String(seconds(at)), "-i", src,
    "-frames:v", "1", "-vf", "scale=-2:min(480\\,ih)", "-q:v", "3",
    "-f", "image2", out,
  ]);
  if (!fs.existsSync(out) || fs.statSync(out).size === 0) throw new Error(`no frame at ${at}`);
  return out;
}

function run(bin: string, args: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(bin, args, { stdio: ["ignore", "ignore", "pipe"] });
    let stderr = "";
    child.stderr?.on("data", (chunk) => { stderr += String(chunk); });
    child.on("error", (error) => reject(new Error(`${bin} failed to start: ${error.message}`)));
    child.on("close", (code) => code === 0 ? resolve()
      : reject(new Error(`${bin} exited ${code}: ${stderr.trim().slice(0, 300)}`)));
  });
}
